<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { classesApi, gradebookApi, classPlannerApi, classNotesApi } from '@/api'
import type { ClassDetails, Student, Gradebook } from '@/api'
import { paletteForKey, paletteFromColour, type PaletteColour, type PaletteHue } from '@/design-system/components'
import ClassWorkspaceHeader, { type ClassTab } from '@/components/classes/ClassWorkspaceHeader.vue'
import ClassSettingsModal from '@/components/modals/ClassSettingsModal.vue'
import { useAppShellStore } from '@/store/AppShellStore'
import StudentsTab from '@/components/classes/tabs/StudentsTab.vue'
import AttendanceTab from '@/components/classes/tabs/AttendanceTab.vue'
import GradesTab from '@/components/classes/tabs/GradesTab.vue'
import ScheduleTab from '@/components/classes/tabs/ScheduleTab.vue'
import ReportsTab from '@/components/classes/tabs/ReportsTab.vue'
import NotesPanel from '@/components/NotesPanel.vue'

const route = useRoute()
const router = useRouter()
const appShell = useAppShellStore()

const classId = computed(() => route.params.classId as string)

const cls = ref<ClassDetails | null>(null)
const students = ref<Student[]>([])
const gradebook = ref<Gradebook | null>(null)
const nextLabel = ref<string | null>(null)
const loading = ref(true)

const VALID: ClassTab[] = ['students', 'attendance', 'grades', 'schedule', 'reports', 'notes']
const activeTab = computed<ClassTab>(() => {
  const t = route.query.tab as string | undefined
  return t && (VALID as string[]).includes(t) ? (t as ClassTab) : 'students'
})
function setTab(tab: ClassTab) {
  router.replace({ query: { ...route.query, tab } })
}

const hue = computed<PaletteHue>(() =>
  cls.value?.colour ? paletteFromColour(cls.value.colour as PaletteColour) : paletteForKey(classId.value),
)

const finalPctById = computed<Record<string, number | null>>(() => {
  const m: Record<string, number | null> = {}
  for (const sg of gradebook.value?.studentGrades ?? []) m[sg.studentId] = sg.finalGrade?.percentage ?? null
  return m
})

function fmtNext(d: Date): string {
  const sameDay = d.toDateString() === new Date().toDateString()
  const time = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
  return `${sameDay ? 'Today' : d.toLocaleDateString('en-GB', { weekday: 'short' })} · ${time}`
}

async function loadNext() {
  const now = new Date()
  const to = new Date(now)
  to.setDate(now.getDate() + 13)
  const iso = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  try {
    const items = await classPlannerApi.getClassPlanner(classId.value, iso(now), iso(to))
    const upcoming = items
      .filter((it) => it.type === 'Lesson')
      .map((it) => new Date(it.dateTime))
      .filter((d) => d >= now)
      .sort((a, b) => a.getTime() - b.getTime())
    nextLabel.value = upcoming[0] ? fmtNext(upcoming[0]) : null
  } catch {
    nextLabel.value = null
  }
}

async function loadGradebook() {
  try {
    gradebook.value = await gradebookApi.getGradebook(classId.value)
  } catch {
    gradebook.value = null
  }
}
async function loadStudents() {
  try {
    const res = await classesApi.classesClassIdStudentsGet(classId.value)
    students.value = (res.data ?? []) as Student[]
  } catch {
    students.value = []
  }
}

async function loadAll() {
  loading.value = true
  try {
    const res = await classesApi.classesClassIdGet(classId.value)
    cls.value = res.data ?? null
  } catch {
    cls.value = null
  }
  await Promise.all([loadStudents(), loadGradebook(), loadNext()])
  loading.value = false
}

watch(classId, loadAll, { immediate: true })

// ── Class settings (kebab menu) ──
const showSettings = ref(false)

function onClassUpdated(updated: ClassDetails) {
  cls.value = updated
  void appShell.loadClasses(true) // rail + overview pick up the new name/colour
}

function onClassDeleted() {
  void appShell.loadClasses(true)
  router.push('/classes')
}
</script>

<template>
  <div class="ws">
    <template v-if="cls">
      <ClassWorkspaceHeader
        :hue="hue"
        :name="cls.name ?? ''"
        :student-count="cls.studentCount ?? students.length"
        :next-label="nextLabel"
        :active-tab="activeTab"
        @select="setTab"
        @take-register="setTab('attendance')"
        @back="router.push('/classes')"
        @settings="showSettings = true"
      />
      <ClassSettingsModal
        v-model="showSettings"
        :cls="cls"
        @updated="onClassUpdated"
        @deleted="onClassDeleted"
      />
      <div class="ws__body">
        <StudentsTab
          v-if="activeTab === 'students'"
          :class-id="classId"
          :hue="hue"
          :students="students"
          :final-pct-by-id="finalPctById"
          @changed="loadStudents(); loadGradebook()"
        />
        <AttendanceTab v-else-if="activeTab === 'attendance'" :class-id="classId" :hue="hue" />
        <GradesTab
          v-else-if="activeTab === 'grades'"
          :class-id="classId"
          :hue="hue"
          :gradebook="gradebook"
          @changed="loadGradebook"
        />
        <ScheduleTab v-else-if="activeTab === 'schedule'" :class-id="classId" :hue="hue" />
        <ReportsTab v-else-if="activeTab === 'reports'" :class-id="classId" :hue="hue" />
        <div v-else-if="activeTab === 'notes'" class="cw__notes">
          <NotesPanel
            :fetch-notes="() => classNotesApi.getNotes(classId)"
            :create-note="(data) => classNotesApi.createNote(classId, data)"
            :update-note="(noteId, data) => classNotesApi.updateNote(classId, noteId, data)"
            :delete-note="(noteId) => classNotesApi.deleteNote(classId, noteId)"
            :accent-color="hue.solid"
            placeholder="Add a class note…"
            empty-message="No class notes yet"
            analytics-event="class_note_added"
          />
        </div>
      </div>
    </template>
    <div v-else-if="loading" class="ws__loading">Loading…</div>
    <div v-else class="ws__loading">Class not found.</div>
  </div>
</template>

<style scoped>
.ws {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: var(--surface);
  overflow: hidden;
}
.ws__body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 22px 26px 32px;
}
.cw__notes {
  max-width: 760px;
}
.ws__loading {
  padding: 48px;
  text-align: center;
  color: var(--ink-400);
  font-family: var(--font-sans);
  font-size: 14px;
}
</style>
